/**
 * This module contains types and functionality for single-choice and
 * multiple-choice quizzes.
 *
 * @module components/choice-quiz
 */

/***/
import Model, {AttributeChangeObservable} from '@lernetz/ts-lib/src/core/model/Model';
import {View, ViewRegistrations} from '@lernetz/ts-lib/src/core/view/View';
import Repeat from '@lernetz/ts-lib/src/components/Repeat';
import Resolve, {Resolvable} from '@lernetz/ts-lib/src/components/Resolve';

import {Quiz, QuizInputState} from '../quiz';

import {
    CheckBoxChecked as checkBoxCheckedTemplate,
    CheckBoxUnchecked as checkBoxUncheckedTemplate,
    ChoiceQuiz as choiceQuizTemplate,
    RadioButtonChecked as radioButtonCheckedTemplate,
    RadioButtonUnchecked as radioButtonUncheckedTemplate,
    MultipleChoiceQuizTextChoice as multipleChoiceQuizTextChoiceTemplate,
    SingleChoiceQuizTextChoice as singleChoiceQuizTextChoiceTemplate,
    Toggle as toggleTemplate} from '../../templates/choice-quiz';


/**
 * A choice in a single-choice or multiple-choice quiz.
 *
 * @see [[TextChoice]], a concrete implementation for textual choices.
 */
export interface Choice extends AttributeChangeObservable, Resolvable {

    /**
     * Gets whether this choice is a (or the) correct one.
     */
    correct: boolean;

    /**
     * Gets or sets whether this choice should be rendered as currently selected.
     *
     * Implementations should raise [[change]] events for this property.
     */
    selected: boolean;
}


/**
 * Specifies the type of a [[ChoiceQuiz]] (either `'single'` or `'multiple'`).
 */
export type ChoiceQuizType = 'multiple' | 'single';


/**
 * Specifies the initial state of a [[ChoiceQuiz]].
 */
export interface ChoiceQuizInitialState {

    /**
     * Gets the available choices for the single-choice quiz.
     */
    choices: Choice[];

    /**
     * Gets the type of choice quiz (`'single'` or `'multiple'`).
     */
    type: ChoiceQuizType;
}


/**
 * A type of [[Quiz]] that models single-choice and multiple-choice quizzes.
 *
 * @see [[Quiz]], the main interface implemented by this class.
 */
export class ChoiceQuiz extends Model implements Quiz, Resolvable {

    private readonly choices_: Choice[];
    private readonly type_: ChoiceQuizType;

    /**
     * Initializes a new instance of the [[ChoiceQuiz]] class.
     *
     * @param initialState Specifies the initial state for the new instance.
     * @param data An optional object to be passed through to the base class' ([[Model]]) constructor.
     */
    public constructor(initialState: ChoiceQuizInitialState, data: object = {}) {
        super(data);
        this.choices_ = initialState.choices;
        this.type_ = initialState.type;

        this.updateInputState();
        for (const ch of this.choices) {
            ch.change.filter(c => c.name === 'selected').subscribe(_ => this.updateInputState());
        }
    }

    /**
     * Gets all choices in the quiz.
     */
    public get choices(): Choice[] {
        return this.choices_;
    }

    /** @inheritdoc */
    public get inputState(): QuizInputState {
        return this.get<QuizInputState>('inputState');
    }

    /**
     * Gets the type of this choice quiz (either `'single'` or `'multiple'`).
     */
    public get type(): ChoiceQuizType {
        return this.type_;
    }

    /** @inheritdoc */
    public getViewName(): string {
        switch (this.type) {

            case 'multiple':
                return 'MultipleChoiceQuiz';

            case 'single':
                return 'SingleChoiceQuiz';
        }
    }

    /**
     * Callback method that gets invoked when the user clicks or taps on the specified `choice`.
     * It sets that choice's [[Choice.selected]] state to `true`, and resets all other choices' `selected` state.
     *
     * @param choice The [[Choice]] that was clicked or tapped by the user.
     */
    public onChoiceClicked(choice: Choice): void {
        switch (this.type) {

            case 'multiple':
                choice.selected = !choice.selected;
                break;

            case 'single':
                for (const ch of this.choices) {
                    ch.selected = (ch === choice);
                }
                break;
        }
    }

    /**
     * Updates the [[inputState]] property.
     */
    protected updateInputState(): void {
        let inputState: QuizInputState;
        if (this.choices.some(ch => ch.selected)) {
            inputState = this.choices.some(ch => ch.selected !== ch.correct) ? 'incorrect' : 'correct';
        }
        else {
            inputState = 'none';
        }
        this.set('inputState', inputState);
    }
}


/**
 * Specifies the initial state of a [[TextChoice]].
 */
export interface TextChoiceInitialState {

    /**
     * Sets whether this choice is a (or the) correct one.
     */
    correct: boolean;

    /**
     * Sets whether this choice should be rendered as initially selected.
     *
     * Defaults to `false`.
     */
    selected?: boolean;

    /**
     * Sets the text that should be displayed for this choice.
     */
    text: string;
}


/**
 * A textual choice in a [[ChoiceQuiz]].
 *
 * @see [[Choice]], the main interface implemented by this class.
 * @see `SingleChoiceQuizTextChoice.html`, a suitable template for rendering this type in the context of a `SingleChoiceQuiz`.
 * @see `MultipleChoiceQuizTextChoice.html`, a suitable template for rendering this type in the context of a `MultipleChoiceQuiz`.
 */
export class TextChoice extends Model implements Choice {

    private readonly correct_: boolean;
    private readonly text_: string;

    /**
     * Initializes a new instance of the [[TextChoice]] class.
     *
     * @param initialState Specifies the initial state for the new instance.
     */
    public constructor(initialState: TextChoiceInitialState) {
        super({});
        this.correct_ = initialState.correct;
        this.selected = initialState.selected || false;
        this.text_ = initialState.text;
    }

    /** @inheritdoc */
    public get correct(): boolean {
        return this.correct_;
    }

    /** @inheritdoc */
    public get selected(): boolean {
        return this.get<boolean>('selected', false);
    }

    public set selected(value: boolean) {
        this.set('selected', value);
    }

    /**
     * Gets the text that should be displayed for this choice.
     */
    public get text(): string {
        return this.text_;
    }

    /** @inheritdoc */
    public getViewName(): string {
        return 'TextChoice';
    }
}


/**
 * A `View` component that renders a toggle (check box or radio button).
 *
 * @hidden
 */
export class ToggleView extends View<Toggle> {

    private readonly checkedTemplate_: string;
    private readonly uncheckedTemplate_: string;

    /**
     * Initializes a new instance of the `ToggleView` class.
     */
    public constructor(checkedTemplate: string, uncheckedTemplate: string) {
        super({ template: toggleTemplate, data: new Toggle() });
        this.checkedTemplate_ = checkedTemplate;
        this.uncheckedTemplate_ = uncheckedTemplate;
    }

    /** @inheritdoc */
    protected init() {
        this.data.change.filter(ch => ch.name === 'checked')
                        .subscribe(_ => this.updateIcon());
        this.updateIcon();
    }

    private updateIcon(): void {
        if (!this.node || !this.data) return;
        this.node.innerHTML = this.data.checked ? this.checkedTemplate_
                                                : this.uncheckedTemplate_;
    }
}


/**
 * A toggle (check box or radio button).
 *
 * @hidden
 */
class Toggle extends Model {

    /**
     * Initializes a new instance of the `Toggle` class.
     */
    public constructor() {
        super({});
    }

    /**
     * Gets or sets whether the toggle is currently checked.
     *
     * A [[change]] event occurs when the value of this property changes.
     */
    public get checked(): boolean {
        return this.get('checked', false);
    }

    public set checked(value: boolean) {
        this.set('checked', value);
    }
}


/** @ignore */
const registrations_ = {

    'CheckBox': () => new ToggleView(checkBoxCheckedTemplate, checkBoxUncheckedTemplate),

    'MultipleChoiceQuiz': {
        template: choiceQuizTemplate,
        registrations: {
            'repeat': Repeat,
            'resolve': Resolve,
            'TextChoice': { template: multipleChoiceQuizTextChoiceTemplate },
        }
    },

    'SingleChoiceQuiz': {
        template: choiceQuizTemplate,
        registrations: {
            'repeat': Repeat,
            'resolve': Resolve,
            'TextChoice': { template: singleChoiceQuizTextChoiceTemplate },
        },
    },

    'RadioButton': () => new ToggleView(radioButtonCheckedTemplate, radioButtonUncheckedTemplate),
};


/**
 * A set of view registrations using the default templates for the views defined in this module.
 *
 * @example
```

  import {register} from '@lernetz/ts-lib/src/core/view/View';
  import {registrations} from '@lernetz/lernfragen/components/choice-quiz';

  register(registrations);
```
 */
export const registrations: ViewRegistrations & typeof registrations_ = registrations_;
